import { useCallback, useEffect, useState } from "react";
import {
  Search,
  Filter,
  ShoppingBag,
  Clock,
  CheckCircle2,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Input } from "@components/ui/Input";
import { Button } from "@components/ui/Button";
import { Card } from "@components/ui/Card";
import { PermissionDenied } from "@components/ui/PermissionDenied";
import { useAuth } from "@contexts/AuthContext";
import { orderService } from "../services/orderService";
import type {
  Order,
  OrderStats,
} from "../types/order";
import { OrdersTable } from "./components/orders/OrdersTable";
import {
  OrderFiltersDialog,
  type OrderFiltersState,
} from "./components/orders/OrderFiltersDialog";
import { OrderDetailsDialog } from "./components/orders/OrderDetailsDialog";
import { OrderRefundDialog } from "./components/orders/OrderRefundDialog";
import { useToast } from "@components/ui/ToastContainer";

const PAGE_SIZE = 20;
const REQUIRED_PERMISSION = "orders:read";

const DEFAULT_FILTERS: OrderFiltersState = {
  status: "all",
  gateway: "all",
};

export default function OrdersPage() {
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useToast();

  const canRefund = hasPermission("orders:refund");

  if (!hasPermission(REQUIRED_PERMISSION)) {
    return <PermissionDenied requiredPermission={REQUIRED_PERMISSION} />;
  }

  return (
    <DashboardLayout>
      <OrdersPageContent
        canRefund={canRefund}
        showSuccess={showSuccess}
        showError={showError}
      />
    </DashboardLayout>
  );
}

interface OrdersPageContentProps {
  canRefund: boolean;
  showSuccess: (msg: string, title?: string) => void;
  showError: (msg: string, title?: string) => void;
}

function OrdersPageContent({
  canRefund,
  showSuccess,
  showError,
}: OrdersPageContentProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [isListLoading, setIsListLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] =
    useState<OrderFiltersState>(DEFAULT_FILTERS);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [refundingOrder, setRefundingOrder] = useState<Order | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchStats = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      const response = await orderService.stats();
      setStats(response.data);
    } catch (err) {
      console.error("Failed to load order stats:", err);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(
    async (page: number) => {
      setIsListLoading(true);
      try {
        const params: Record<string, string | number> = {
          page,
          limit: PAGE_SIZE,
        };
        if (filters.status !== "all") {
          params.status = filters.status;
        }
        if (filters.gateway !== "all") {
          params.gateway = filters.gateway;
        }

        const response = await orderService.list(params);
        const list = response.orders || [];
        setOrders(list);
        setTotalPages(response.totalPages || 1);
        setTotalItems(response.total || list.length);
      } catch (err) {
        console.error("Failed to load orders:", err);
        showError("Failed to load orders. Please try again.", "Load Failed");
        setOrders([]);
      } finally {
        setIsListLoading(false);
      }
    },
    [filters, showError],
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setCurrentPage(1);
    fetchOrders(1);
  }, [fetchOrders]);

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage, fetchOrders]);

  // Client-side filter for the "search by order id/user" affordance.
  const filteredOrders = orders.filter((order) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const matchesId = order._id.toLowerCase().includes(term);
    const matchesCoupon = order.coupon?.code
      ?.toLowerCase()
      .includes(term);
    if (typeof order.user === "string") {
      return matchesId || !!matchesCoupon;
    }
    const matchesEmail = order.user.email?.toLowerCase().includes(term);
    const matchesName = `${order.user.firstName ?? ""} ${
      order.user.lastName ?? ""
    }`
      .toLowerCase()
      .includes(term);
    return matchesId || matchesEmail || matchesName || !!matchesCoupon;
  });

  const handleView = (order: Order) => {
    setViewingOrderId(order._id);
    setViewingOrder(order);
  };

  const handleRefund = (order: Order) => {
    setRefundingOrder(order);
  };

  const closeDetails = () => {
    setViewingOrderId(null);
    setViewingOrder(null);
  };

  const countByStatus = (
    key: keyof OrderStats["counts"],
    fallback = 0,
  ): number => {
    if (!stats?.counts) return fallback;
    const value = stats.counts[key];
    return typeof value === "number" ? value : fallback;
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== "all",
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Orders
        </h2>
        <p className="text-gray-500">
          Review all cart checkouts, track status, and issue refunds.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-100 rounded-full">
              <ShoppingBag className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {isStatsLoading
                  ? "..."
                  : (stats
                      ? stats.counts.paid +
                        stats.counts.pending +
                        stats.counts.cancelled +
                        stats.counts.expired +
                        stats.counts.refunded
                      : 0
                    ).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {isStatsLoading ? "..." : countByStatus("paid")}
              </p>
              <p className="text-xs text-gray-400">
                ${stats?.totalRevenueUSD?.toLocaleString() || 0} revenue
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {isStatsLoading ? "..." : countByStatus("pending")}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <RotateCcw className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Refunded</p>
              <p className="text-2xl font-bold text-gray-900">
                {isStatsLoading ? "..." : countByStatus("refunded")}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-200 rounded-full">
              <XCircle className="w-6 h-6 text-gray-700" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cancelled</p>
              <p className="text-2xl font-bold text-gray-900">
                {isStatsLoading ? "..." : countByStatus("cancelled")}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
        <div className="w-full sm:w-[408px]">
          <Input
            placeholder="Search by order id, user, or coupon code..."
            icon={<Search size={20} />}
            className="bg-white"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto pb-2 sm:pb-0">
          <Button
            variant="outline"
            size="sm"
            className={`h-[39px] px-4 gap-2 ${
              activeFilterCount > 0
                ? "border-[#102040] text-[#102040] bg-blue-50"
                : ""
            }`}
            onClick={() => setIsFilterOpen(true)}
          >
            <Filter size={16} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="flex items-center justify-center w-5 h-5 ml-1 text-[10px] font-bold text-white bg-[#102040] rounded-full">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      <OrdersTable
        orders={filteredOrders}
        isLoading={isListLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={PAGE_SIZE}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onView={handleView}
        onRefund={(order) => {
          if (!canRefund) {
            showError(
              "You do not have permission to issue refunds.",
              "Permission denied",
            );
            return;
          }
          handleRefund(order);
        }}
      />

      <OrderFiltersDialog
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onApply={(newFilters) => {
          setFilters(newFilters);
          setCurrentPage(1);
        }}
      />

      <OrderDetailsDialog
        isOpen={viewingOrderId !== null}
        onClose={closeDetails}
        orderId={viewingOrderId}
        initialOrder={viewingOrder}
      />

      {refundingOrder && canRefund && (
        <OrderRefundDialog
          isOpen={refundingOrder !== null}
          onClose={() => setRefundingOrder(null)}
          onSuccess={() => {
            showSuccess(
              `Order refunded successfully (${refundingOrder._id.slice(0, 8)}…).`,
              "Refund complete",
            );
            fetchStats();
            fetchOrders(currentPage);
            setRefundingOrder(null);
          }}
          order={refundingOrder}
        />
      )}
    </div>
  );
}
